import { createContext, useCallback, useContext } from 'react';
import useApiService from './ApiService';
import CONFIG from './CONFIG';
import {
  IPCollectionApi,
  IPCollectionApiList,
  IPCollectionCreateApi,
  IPCollectionPackagesApiList,
  IPCollectionUpdateApi,
} from './IPCollectionsApiProviderTypes';

interface IPCollectionsApiContextType {
  getIPCollections: (skip?: number, limit?: number) => Promise<IPCollectionApiList>;
  deleteIPCollection: (collectionId: string) => Promise<void>;
  createIPCollection: (data: IPCollectionCreateApi) => Promise<IPCollectionApi>;
  updateIPCollection: (
    collectionId: string,
    data: IPCollectionUpdateApi,
  ) => Promise<IPCollectionApi>;
  getIPCollectionById: (collectionId: string) => Promise<IPCollectionApi>;
  getIPCollectionPackages: (
    collectionId: string,
    skip?: number,
    limit?: number,
  ) => Promise<IPCollectionPackagesApiList>;
}

const IPCollectionsApiContext = createContext<IPCollectionsApiContextType | null>(null);

export function IPCollectionsApiProvider({ children }: { children: React.ReactNode }) {
  const { get, post, del, put } = useApiService(CONFIG.SERVER_DOCUMENT);

  const getIPCollections = useCallback(
    async (skip: number = 0, limit: number = 10): Promise<IPCollectionApiList> => {
      try {
        const response = await get(
          `/v1/information_package_collection?skip=${skip}&limit=${limit}`,
        );
        return response.data;
      } catch (error) {
        console.error('Error fetching information package collections:', error);
        throw error;
      }
    },
    [get],
  );

  const deleteIPCollection = useCallback(
    async (collectionId: string): Promise<void> => {
      try {
        await del(`/v1/information_package_collection/${collectionId}`);
      } catch (error) {
        console.error('Error deleting information package collection:', error);
        throw error;
      }
    },
    [del],
  );

  const createIPCollection = useCallback(
    async (data: IPCollectionCreateApi): Promise<IPCollectionApi> => {
      try {
        const response = await post(`/v1/information_package_collection`, data);
        return response.data;
      } catch (error) {
        console.error('Error creating information package collection:', error);
        throw error;
      }
    },
    [post],
  );
  const updateIPCollection = useCallback(
    async (collectionId: string, data: IPCollectionUpdateApi): Promise<IPCollectionApi> => {
      try {
        const response = await put(`/v1/information_package_collection/${collectionId}`, data);
        return response.data;
      } catch (error) {
        console.error('Error updating information package collection:', error);
        throw error;
      }
    },
    [put],
  );

  const getIPCollectionById = useCallback(
    async (collectionId: string): Promise<IPCollectionApi> => {
      try {
        const response = await get(`/v1/information_package_collection/${collectionId}`);
        return response.data;
      } catch (error) {
        console.error('Error fetching information package collection by ID:', error);
        throw error;
      }
    },
    [get],
  );

  const getIPCollectionPackages = useCallback(
    async (
      collectionId: string,
      skip: number = 0,
      limit: number = 10,
    ): Promise<IPCollectionPackagesApiList> => {
      try {
        const response = await get(
          `/v1/information_package_collection/${collectionId}` +
            `/packages?skip=${skip}&limit=${limit}`,
        );
        return response.data;
      } catch (error) {
        console.error('Error fetching information packages in collection:', error);
        throw error;
      }
    },
    [get],
  );

  return (
    <IPCollectionsApiContext.Provider
      value={{
        getIPCollections,
        deleteIPCollection,
        createIPCollection,
        updateIPCollection,
        getIPCollectionById,
        getIPCollectionPackages,
      }}
    >
      {children}
    </IPCollectionsApiContext.Provider>
  );
}

export function useIPCollectionsApi(): IPCollectionsApiContextType {
  const context = useContext(IPCollectionsApiContext);
  if (!context) {
    throw new Error('useIPCollectionsApi must be used within a IPCollectionsApiProvider');
  }
  return context;
}
